import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export default function ProjectDomainsPage() {
  const navigate   = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/partners/project-domains`, { withCredentials: true })
      .then(r => {
        setProjects(r.data.projects || []);
        if (r.data.projects?.length > 0) setActiveTab(r.data.projects[0].project_id);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const activeProject = useMemo(
    () => projects.find(p => p.project_id === activeTab) || null,
    [projects, activeTab]
  );

  // Client-side search within the active tab
  const filteredDomains = useMemo(() => {
    if (!activeProject) return [];
    if (!search.trim()) return activeProject.domains;
    const q = search.toLowerCase();
    return activeProject.domains.filter(d => d.domain?.toLowerCase().includes(q));
  }, [activeProject, search]);

  const handleTabChange = (pid) => {
    setActiveTab(pid);
    setSearch('');
  };

  return (
    <div data-testid="project-domains-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt;{' '}
        <span style={{ cursor: 'pointer', color: '#3182ce' }} onClick={() => navigate('/partners')} data-testid="project-back-link">
          Partner Dashboard
        </span>{' '}
        &gt; Project Domains
      </div>

      <div className="page-header">
        <h1>Project Domains</h1>
        <p>Domains scoped to specific partner programmes</p>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : projects.length === 0 ? (
        <div className="empty-state"><h3>No projects found</h3></div>
      ) : (
        <>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0' }}>
            {projects.map(p => (
              <button
                key={p.project_id}
                data-testid={`project-tab-${p.project_id}`}
                onClick={() => handleTabChange(p.project_id)}
                style={{
                  padding: '8px 18px',
                  background: 'none', border: 'none',
                  cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  fontFamily: '"DM Sans", sans-serif',
                  color: activeTab === p.project_id ? '#1a7f64' : '#718096',
                  borderBottom: activeTab === p.project_id ? '2px solid #1a7f64' : '2px solid transparent',
                  marginBottom: -2,
                  transition: 'color 0.15s, border-color 0.15s',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.project_name}
                <span style={{
                  marginLeft: 7,
                  background: activeTab === p.project_id ? '#e6f7f2' : '#f1f5f9',
                  color: activeTab === p.project_id ? '#1a7f64' : '#718096',
                  fontSize: 11, fontWeight: 700,
                  padding: '1px 7px', borderRadius: 10,
                }}>
                  {p.domain_count.toLocaleString()}
                </span>
              </button>
            ))}
          </div>

          {/* Search bar for active tab */}
          {activeProject && (
            <>
              <div className="filter-bar" style={{ alignItems: 'flex-end', marginBottom: 16 }}>
                <div className="filter-group" style={{ flex: 3 }}>
                  <label className="filter-label">Search domain in {activeProject.project_name}</label>
                  <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                    <input
                      type="text"
                      className="filter-input"
                      style={{ paddingLeft: 30 }}
                      placeholder="Filter domains…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      data-testid="project-search-input"
                    />
                  </div>
                </div>
                {search && (
                  <button className="btn btn-secondary" onClick={() => setSearch('')} data-testid="project-search-reset">
                    Reset
                  </button>
                )}
              </div>

              <div className="table-container">
                <div className="table-header">
                  <div>
                    <span className="table-title">{activeProject.project_name}</span>
                    <span className="table-count" style={{ marginLeft: 8 }}>
                      ({filteredDomains.length.toLocaleString()} of {activeProject.domain_count.toLocaleString()} domains)
                    </span>
                  </div>
                </div>

                {filteredDomains.length === 0 ? (
                  <div className="empty-state">
                    <h3>No domains match</h3>
                    <p>Try a different search term</p>
                  </div>
                ) : (
                  <div className="table-scroll">
                    <table data-testid={`project-table-${activeTab}`}>
                      <thead>
                        <tr>
                          <th>DOMAIN</th>
                          <th>PARTNER ONE ID</th>
                          <th>PARTNER NAME</th>
                          <th>ADDED</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDomains.map((d, i) => (
                          <tr key={i} data-testid={`project-row-${i}`}>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.domain}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.partner_one_id ?? '—'}</td>
                            <td>{d.esi_partner_name || <span style={{ color: '#ccc' }}>—</span>}</td>
                            <td style={{ fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>{formatDate(d.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
